/**
 * @file Generic helpers for composing parent-board adapter objects.
 */

import { getBoardRegistryEntry } from "./boardRegistry.js";
import { getBoardTableColumnDefinitions } from "./boardTableColumns.js";
import {
  getBoardFilterDefinitions,
  getBoardSortOptions,
} from "./boardRuntimeOptions.js";

export const PARENT_BOARD_ADAPTER_CONTRACT_VERSION = 1;

/**
 * @typedef {object} ParentBoardEmptyState
 * @property {string} title - Empty-state title.
 * @property {string} body - Empty-state body copy.
 * @property {string} [tone] - Optional shared state-card tone.
 */

/**
 * @typedef {object} ParentBoardRouteMetadata
 * @property {string} viewKey - App route/view key.
 * @property {string} itemType - Singular item type used by details and DOM IDs.
 * @property {string} collectionName - Firestore collection name.
 * @property {string|null} permissionKey - Permission required for the module.
 */

/**
 * @typedef {object} ParentBoardCardAdapter
 * @property {Function} renderCard - Renders a single board card.
 * @property {Function} [renderWipCard] - Renders a single WIP board card.
 */

/**
 * @typedef {object} ParentBoardTableRowAdapter
 * @property {Function} renderRow - Renders a single table row.
 * @property {Function} [renderCell] - Renders a single table cell.
 */

/**
 * @typedef {object} ParentBoardColumnAdapter
 * @property {Function} getDefinitions - Returns table column labels by key.
 * @property {Array<string>} defaultVisibleColumns - Default visible columns.
 */

/**
 * @typedef {object} ParentBoardControlAdapter
 * @property {Function} getSortOptions - Returns supported sort options.
 * @property {Function} getFilterDefinitions - Returns supported filters.
 */

/**
 * @typedef {object} ParentBoardRendererAdapter
 * @property {Function} table - Renders the table layout.
 * @property {Function} kanban - Renders the normal kanban layout.
 * @property {Function} [wipKanban] - Renders the WIP kanban layout.
 */

/**
 * @typedef {object} ParentBoardAdapter
 * @property {number} contractVersion - Contract version implemented.
 * @property {string} adapterKey - Registry adapter key.
 * @property {ParentBoardRouteMetadata} route - Route and collection metadata.
 * @property {object} capabilities - Board capability flags from metadata.
 * @property {ParentBoardCardAdapter|null} cards - Card rendering adapter.
 * @property {ParentBoardTableRowAdapter|null} tableRows - Table row adapter.
 * @property {ParentBoardColumnAdapter} columns - Table column adapter.
 * @property {ParentBoardControlAdapter} controls - Board control adapter.
 * @property {Function} resolveEmptyState - Optional empty-state resolver.
 * @property {ParentBoardRendererAdapter} renderers - Board renderers.
 */

function resolveEmptyState() {
  return null;
}

/**
 * Creates a parent-board adapter around the current shared board renderers.
 * Domain adapters use this as a migration wrapper until card and row rendering
 * move behind domain-owned implementations.
 * @param {object} options - Adapter composition options.
 * @param {string} options.adapterKey - Board registry adapter key.
 * @param {string} options.viewKey - Board view key.
 * @param {ParentBoardRendererAdapter} options.renderers - Board renderers.
 * @param {ParentBoardCardAdapter|null} [options.cards] - Card adapter.
 * @param {ParentBoardTableRowAdapter|null} [options.tableRows] - Row adapter.
 * @param {Function} [options.resolveEmptyState] - Empty-state resolver.
 * @returns {ParentBoardAdapter} Parent-board adapter.
 */
export function createParentBoardAdapter({
  adapterKey,
  viewKey,
  renderers,
  cards = null,
  tableRows = null,
  resolveEmptyState: resolveDomainEmptyState = resolveEmptyState,
}) {
  const board = getBoardRegistryEntry(viewKey);

  return Object.freeze({
    contractVersion: PARENT_BOARD_ADAPTER_CONTRACT_VERSION,
    adapterKey,
    route: Object.freeze({
      viewKey,
      itemType: board.itemType,
      collectionName: board.collectionName,
      permissionKey: board.permissionKey,
    }),
    capabilities: board.capabilities,
    cards,
    tableRows,
    columns: Object.freeze({
      defaultVisibleColumns: board.defaultViewSettings.visibleColumns,
      getDefinitions: () => getBoardTableColumnDefinitions(viewKey),
    }),
    controls: Object.freeze({
      getSortOptions: () => getBoardSortOptions(viewKey),
      getFilterDefinitions: () => getBoardFilterDefinitions(viewKey),
    }),
    resolveEmptyState: resolveDomainEmptyState,
    renderers: Object.freeze(renderers),
  });
}
